import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../../Common/api/ABHub/ABHubGlobalConfig';

interface CacheEntry<T> {
    data: T;
    savedAt: number;
}

const STORAGE_KEY = 'sre-triage-cache-v2';
const MAX_ENTRIES = 30;
const MAX_CHARACTERS = 1500000;
export const SRE_CACHE_MAX_AGE_MS = 60000;

const isFreshEntry = (value: unknown): value is CacheEntry<unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const entry = value as Partial<CacheEntry<unknown>>;
    const now = Date.now();
    return (
        Object.prototype.hasOwnProperty.call(entry, 'data') &&
        typeof entry.savedAt === 'number' &&
        Number.isFinite(entry.savedAt) &&
        entry.savedAt <= now &&
        now - entry.savedAt <= SRE_CACHE_MAX_AGE_MS
    );
};

export const sreCacheScope = (): string =>
    JSON.stringify([
        getConfig(GlobalConfigProperties.Cloud),
        getConfig(GlobalConfigProperties.Environment),
        getConfig(GlobalConfigProperties.AgrmUrl),
    ]);

const readEntries = (): Record<string, CacheEntry<unknown>> => {
    try {
        const serialized = window.sessionStorage.getItem(STORAGE_KEY) ?? '{}';
        if (serialized.length > MAX_CHARACTERS) return {};
        const entries = JSON.parse(serialized);
        return entries && typeof entries === 'object' && !Array.isArray(entries)
            ? entries
            : {};
    } catch {
        return {};
    }
};

export const readSreCache = <T>(key: string): CacheEntry<T> | undefined => {
    const entry = readEntries()[key];
    if (!isFreshEntry(entry)) return undefined;
    return entry as CacheEntry<T>;
};

export const writeSreCache = <T>(key: string, data: T): void => {
    try {
        const entries = readEntries();
        entries[key] = { data, savedAt: Date.now() };
        const retained = Object.entries(entries)
            .filter(([, entry]) => isFreshEntry(entry))
            .sort((left, right) => right[1].savedAt - left[1].savedAt)
            .slice(0, MAX_ENTRIES);
        let serialized = JSON.stringify(Object.fromEntries(retained));
        while (serialized.length > MAX_CHARACTERS && retained.length) {
            retained.pop();
            serialized = JSON.stringify(Object.fromEntries(retained));
        }
        window.sessionStorage.setItem(STORAGE_KEY, serialized);
    } catch {
        // Requests still work when browser storage is unavailable.
    }
};
